import React, { useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import CheckmarkImage from '../../icons/big_checkmark.svg?react'
import LeftArrow from '../../icons/left-arrow.svg?react'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSButton, PSButtonPrimary } from '../app/PSButton'
import { SelectOption } from '../settings/Options'

type LocationStateType = {
    reason?: SelectOption
}

const viewWillAppear = async (base: PocketComponent) => {
    const locationState = base.locationState as LocationStateType | undefined

    if (!locationState?.reason) {
        return new RecordNotFoundError()
    }

    return { reason: locationState.reason.value }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const user = base.me
    const navigate = usePSNavigate()
    const [showDone, setShowDone] = useState(false)

    const subtitle = useMemo(() => {
        switch (data.reason) {
            case 'covid19':
                return "Covid-19 has been hard on everyone! We're giving Pros impacted by Covid-19 90% off their PocketSuite Premium subscription for 2 months."
            case 'more_time':
                return "Take your time setting up. Here's 2 months of Premium for $1.99/mo."
            case 'break':
                return "You deserve a break. Here's 2 months of Premium for $1.99/mo."
            default:
                return "We understand and we have your back. Here's 2 months of Premium for $1.99/mo."
        }
    }, [data.reason])

    const getDiscount = async () => {
        base.showProgressBar()
        const result = await RecordFactory.getInstance().discountPremium(user, data.reason)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        setShowDone(true)
    }

    return (
        <div className="supportSettings">
            <div className="header">
                {!showDone && (
                    <PSButton className="left" onClick={() => navigate(-1)}>
                        <LeftArrow />
                    </PSButton>
                )}
                <h1 className="title sm">PocketSuite for $1.99</h1>
                {!showDone && (
                    <div className="right">
                        <PSButton
                            onClick={() =>
                                navigate('/settings/change-plan/cancel/confirm', {
                                    state: { reason: data.reason },
                                })
                            }
                        >
                            Continue cancellation
                        </PSButton>
                        <PSButtonPrimary onClick={getDiscount}>Get 90% off</PSButtonPrimary>
                    </div>
                )}
            </div>
            <div className="separator" />
            {!showDone && (
                <>
                    <p className="details">{subtitle}</p>
                    <div className="supportCard column">
                        <div className="title">PocketSuite Premium</div>
                        <div className="details">
                            Access PocketSuite Premium for just $1.99/mo for the next 2 months
                        </div>
                    </div>
                    <div className="supportCard column">
                        <div className="title">Keep your data</div>
                        <div className="details">
                            Keep all of your client data and access all of your Premium features
                        </div>
                    </div>
                </>
            )}
            {showDone && (
                <div className="column center margin">
                    <CheckmarkImage />
                    <h1 className="heading">All done!</h1>
                    <p className="text">
                        You've just unlocked 90% off your current subscription for the next 2
                        months!
                    </p>
                </div>
            )}
        </div>
    )
}

export const DowngradeDiscountPage = usePSComponent(render, viewWillAppear)
